'use client';
import React, { useEffect, useState } from 'react';
import { CalendarBlank } from '@phosphor-icons/react';
import { useRouter } from 'next/navigation';
import { format } from 'date-fns';
import type { Block, PostMeta } from '@/components/blog-editor/types';
import type { RelatedPost } from '@/lib/related-posts';
import { brandExternalUrl } from '@/lib/brand-urls';
import ErrorBoundary from '@/components/error-boundary';
import { BlogListenButtonAuto } from '@/components/blog-sections/BlogListenButtonAuto';
import type { Patch } from '@/components/onpage-editor/EditorContext';
import { EditableText } from '@/components/onpage-editor/EditableText';
import { EditableImage } from '@/components/onpage-editor/EditableImage';
import type { BlogSettingsData } from '@/lib/blog-settings';
import { BlockRenderer } from '@/components/blog-editor/BlockRenderer';
import { ReadOnlyEditorProvider } from '@/components/blog-editor/BlogEditor';
import { toEditorial, toCategorySlug, NEWSROOM_CATEGORY } from '@/lib/editorial-categories';
import { newsSubCategory, newsSubCategorySlug } from '@/lib/news-categories';

// ── Seasons chrome + sections (verbatim from seasons-component-library) ──
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { Footer } from '@/components/seasons/Footer';
import { FlowNavBar } from '@/components/seasons/FlowNavBar';
import { FlowFooter } from '@/components/seasons/FlowFooter';
import { CometNavBar } from '@/components/seasons/CometNavBar';
import { CometFooter } from '@/components/seasons/CometFooter';
import { GalaxyNavBar } from '@/components/seasons/GalaxyNavBar';
import { GalaxyFooter } from '@/components/seasons/GalaxyFooter';
import { GravityNavBar } from '@/components/seasons/GravityNavBar';
import { GravityFooter } from '@/components/seasons/GravityFooter';
import { StoryhealthNavBar } from '@/components/seasons/StoryhealthNavBar';
import { StoryhealthFooter } from '@/components/seasons/StoryhealthFooter';
import { BlogReadingProgress } from '@/components/seasons/BlogReadingProgress';
import { BlogBackToTop } from '@/components/seasons/BlogBackToTop';
import { BlogShareInline } from '@/components/seasons/BlogShareInline';
import { BlogTOC, type TOCItem } from '@/components/seasons/BlogTOC';
import { SuggestedResources, type ResourceCard } from '@/components/seasons/SuggestedResources';
import { NewsletterInline } from '@/components/seasons/NewsletterInline';

interface ClientPostProps {
  meta: PostMeta;
  blocks: Block[];
  suggestedPosts: RelatedPost[];
  slug: string;
  blogSettings?: BlogSettingsData | null;
  sectionLabel?: string;
  sectionHref?: string;
  urlBrand?: string;
  /** When set, appended as &brand=<brandParam> to all listing links (used for gravity). */
  brandParam?: string;
  /** Read server-side in page.tsx from the request headers — see getClientIp. */
  visitorIp?: string;
}

const fmPatch = (path: string, value: any): Patch => ({ kind: 'frontmatter', path, value });

const TOC_HEADING_RE = /^\s*(quick\s+navigation|table\s+of\s+contents|in\s+this\s+article|topics\s+covered)\s*:?\s*$/i;

/* ── Blocks rendered in read-only mode, with inline TOC sections filtered out ── */
function ReadOnlyBlocks({ blocks }: { blocks: Block[] }) {
  const filtered: Block[] = [];
  let skipList = false;
  for (const block of blocks) {
    if (block.type === 'h2' && TOC_HEADING_RE.test((block as any).text || '')) {
      skipList = true;
      continue;
    }
    if (skipList && (block.type === 'ul' || block.type === 'ol')) {
      continue;
    }
    skipList = false;
    filtered.push(block);
  }
  return (
    <ReadOnlyEditorProvider blocks={filtered}>
      {filtered.map(block => (
        <BlockRenderer key={block.id} block={block} />
      ))}
    </ReadOnlyEditorProvider>
  );
}

export default function PostClientPage(props: ClientPostProps) {
  const post = props.meta;
  // urlBrand (from rewrite URL) wins over frontmatter brand so /comet/... always shows Comet chrome
  const effectiveBrand = (props.urlBrand ?? '') || (post.brand ?? '');
  const isFlow = effectiveBrand === 'flow';
  const isComet = effectiveBrand === 'comet';
  const isGalaxy = effectiveBrand === 'galaxy';
  const isGravity = effectiveBrand === 'gravity';
  const isStoryhealth = effectiveBrand === 'storyhealth';
  const router = useRouter();
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);

  const brandSuffix = props.brandParam ? `&brand=${props.brandParam}` : '';

  // Newsroom articles get a "Newsroom > Announcements/Awards" breadcrumb instead of
  // "Stories > Newsroom" — Announcements vs Awards is inferred from the title, same
  // as the /news listing's own category tabs (lib/news-categories.ts).
  const isNewsroomPost = toEditorial(post.category || '', post.contentType) === NEWSROOM_CATEGORY;
  // A post can belong to multiple /news tabs; the breadcrumb only has room for one, so show the first.
  const newsCategory = isNewsroomPost ? (post.newsroomType?.[0] || newsSubCategory(post.title || '')) : null;

  const handleNavSearch = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    const target = props.sectionHref ?? '/blogs';
    router.push(`${target}?q=${encodeURIComponent(trimmed)}${brandSuffix}`);
  };

  // Build TOC from H2/H3 headings inside the article body, after blocks mount.
  useEffect(() => {
    const article = document.getElementById('blog-article-body');
    if (!article) return;
    const els = Array.from(article.querySelectorAll('h2,h3')) as HTMLElement[];
    els.forEach((el, i) => { if (!el.id) el.id = `heading-${i}`; });
    const items: TOCItem[] = els.map(el => ({
      id: el.id,
      label: el.textContent || '',
      level: (parseInt(el.tagName[1]) || 2) as 2 | 3,
    }));
    setTocItems(items);
  }, [props.blocks]);

  const date = post.date ? new Date(post.date) : null;
  const formattedDate = date && !isNaN(date.getTime()) ? format(date, 'MMMM d, yyyy') : '';
  const isoDate = date && !isNaN(date.getTime()) ? date.toISOString().split('T')[0] : '';

  const newsletter = {
    heading: 'Stay connected with Innovaccer',
    body: 'Subscribe to receive the latest insights, updates, and stories from healthcare innovation.',
  };

  const authors: Array<{ name?: string; avatar?: string; profileUrl?: string }> =
    (post.authors && post.authors.length > 0)
      ? post.authors.map(a => ({ ...a, name: a.name?.replace(/^By\s+/i, '') }))
      : [];

  // Map RelatedPost → Seasons ResourceCard shape
  const suggestedCards: ResourceCard[] = props.suggestedPosts.slice(0, 3).map(p => {
    const pDate = p.date ? new Date(p.date) : null;
    const pFormatted = pDate && !isNaN(pDate.getTime()) ? format(pDate, 'MMM d, yyyy') : '';
    const authorLabel = p.authorName
      ? (p.authorCount ?? 1) > 1 ? `${p.authorName} +${(p.authorCount ?? 1) - 1}` : p.authorName
      : undefined;
    return {
      category: p.category ? toEditorial(p.category, p.contentType) : '',
      title: p.title,
      dek: p.dek || undefined,
      href: brandExternalUrl(p.brand, p.slug, p.category) ?? `${props.sectionHref ?? '/blogs'}/${p.slug}`,
      imageSrc: p.heroImg || '',
      authorName: authorLabel,
      date: pFormatted,
      readMinutes: p.readMinutes || undefined,
    };
  });

  return (
    <ErrorBoundary>
      <div className="blog-page">

        <a href="#blog-main" className="blog-skip-link">Skip to article</a>

        {/* ── Seasons BlogNavBar with Resources mega-menu + search + CTAs ── */}
        {isFlow
          ? <FlowNavBar />
          : isComet
          ? <CometNavBar />
          : isGalaxy
          ? <GalaxyNavBar />
          : isGravity
          ? <GravityNavBar />
          : isStoryhealth
          ? <StoryhealthNavBar />
          : <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/request-a-demo" storiesHref="/blogs" />}
        <BlogReadingProgress />

        <main id="blog-main">

          {/* ── Article header ── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-default)',
              paddingTop: 'var(--seasons-space-20)',
              paddingBottom: 'var(--seasons-space-16)',
            }}
          >
            <div className="blog-article-header">
              {/* Breadcrumb — Newsroom > Announcements/Awards, or STORIES > Category.
                  Hidden for Story Health for now (no category/editorial breadcrumb yet). */}
              {!isStoryhealth && (
                <div className="wbn-od-breadcrumb" style={{ justifyContent: 'center', marginBottom: 'var(--seasons-space-6)' }}>
                  <a href={props.sectionHref ?? '/blogs'} className="wbn-od-breadcrumb__link">
                    {isNewsroomPost ? 'Newsroom' : (props.sectionLabel ?? 'Stories')}
                  </a>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--seasons-color-text-tertiary)' }}>
                    <path d="M4.5 2.5L7.5 6l-3 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <a
                    href={
                      isNewsroomPost && newsCategory
                        ? `${props.sectionHref ?? '/news'}?category=${newsSubCategorySlug(newsCategory)}${brandSuffix}`
                        : `${props.sectionHref ?? '/blogs'}?category=${toCategorySlug(post.category || '', post.contentType)}${brandSuffix}`
                    }
                    className="wbn-od-breadcrumb__link"
                  >
                    {isNewsroomPost && newsCategory ? newsCategory : toEditorial(post.category || '', post.contentType)}
                  </a>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                <EditableText
                  id="fm:title"
                  as="h1"
                  className="type-prose-display"
                  value={post.title || ''}
                  multiline
                  richText
                  placeholder="Article title"
                  style={{
                    color: 'var(--seasons-color-prose-heading)',
                    maxWidth: 'none',
                    marginBottom: 'var(--seasons-space-6)',
                  }}
                  buildPatch={(nv) => fmPatch('title', nv)}
                />

                {(authors.length > 0 || formattedDate) && (
                  <div className="blog-byline-row">
                    {authors.length > 0 && (
                      <div className="blog-byline" style={{ fontSize: 'var(--seasons-font-size-16)' }}>
                        <span className="blog-byline__by">By</span>
                        {authors.map((a, idx) => {
                          const last = idx === authors.length - 1;
                          const secondToLast = idx === authors.length - 2;
                          return (
                            <span key={idx} className="blog-byline__author">
                              <EditableText
                                id={`fm:authors.${idx}.name`}
                                as="span"
                                className="blog-author-link"
                                value={a.name || ''}
                                placeholder="Author name"
                                buildPatch={(nv) => fmPatch(`authors.${idx}.name`, nv)}
                              />
                              {!last && (
                                <span className="blog-byline__sep">{secondToLast ? ' and ' : ', '}</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {formattedDate && (
                      <div className="wbn-od-meta" style={{ justifyContent: 'center' }}>
                        <span className="wbn-od-meta__item">
                          <CalendarBlank size={14} weight="regular" aria-hidden="true" />
                          <time dateTime={isoDate}>{formattedDate}</time>
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* ── Hero cover ── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-default)',
              paddingBottom: 'var(--seasons-space-12)',
            }}
          >
            <figure className="blog-hero-cover">
              <EditableImage
                id="fm:heroImg"
                src={post.heroImg || ''}
                alt={post.heroAlt || post.title || 'Article cover image'}
                buildPatch={(nv) => fmPatch('heroImg', nv)}
                className="blog-hero-cover__media"
                style={{ display: 'block', width: '100%' }}
                renderAsBackground={(src) =>
                  src ? (
                    <img
                      src={src}
                      alt={post.heroAlt || post.title || 'Article cover image'}
                      className="blog-hero-cover__img blog-hero-cover__img--loaded"
                    />
                  ) : null
                }
              />
            </figure>
          </section>

          {/* ── Listen + Share ── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-default)',
              borderTop: 'var(--seasons-border-width-default) solid var(--seasons-color-border-default)',
              paddingBottom: 'var(--seasons-space-8)',
            }}
          >
            <div className="blog-listen-section">
              <div className="blog-listen-row">
                <BlogListenButtonAuto
                  slug={props.slug}
                  audioUrl={post.audioUrl || undefined}
                  audioTimings={post.audioTimings || undefined}
                  targetSelector="article.blog-prose"
                />
                <div className="blog-share-group" style={{ display: 'flex', alignItems: 'center', gap: 'var(--seasons-space-3)', marginLeft: 'auto' }}>
                  <span className="wbn-share-label">Share</span>
                  <BlogShareInline title={post.title || ''} />
                </div>
              </div>
            </div>
          </section>

          {/* ── Reading body — single prose column, no aside ── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-default)',
              paddingTop: 'var(--seasons-space-8)',
              paddingBottom: 'var(--seasons-space-20)',
            }}
          >
            <div className="blog-reading-layout">
              <div className="blog-reading-layout__main">
                <article className="blog-prose" id="blog-article-body">
                  <ReadOnlyBlocks blocks={props.blocks} />
                </article>

                {post.tags && post.tags.length > 0 && (
                  <div className="blog-article-tags">
                    <span className="blog-article-tags__label">Topics</span>
                    {post.tags.map((t) => t.tag && (
                      <a
                        key={t.tag}
                        href={`${props.sectionHref ?? '/blogs'}?q=${encodeURIComponent(t.tag)}${brandSuffix}`}
                        className="blog-tag blog-tag--muted"
                      >
                        {t.tag}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* ── Newsletter — constrained to prose column width via blog-newsletter-section grid ── */}
          <section
            id="newsletter"
            style={{
              background: 'var(--seasons-color-surface-default)',
              paddingTop: 0,
              paddingBottom: 'var(--seasons-space-16)',
            }}
          >
            <div className="blog-newsletter-section">
              <div className="blog-newsletter-inner">
                <NewsletterInline
                  variant="compact-card"
                  heading={newsletter.heading}
                  body={newsletter.body}
                  visitorIp={props.visitorIp}
                />
              </div>
            </div>
          </section>

          {/* ── Continue reading — uses Seasons SuggestedResources component ── */}
          {suggestedCards.length > 0 && (
            <SuggestedResources
              heading="Keep reading"
              cards={suggestedCards}
              viewAllHref={`${props.sectionHref ?? '/blogs'}${props.brandParam ? `?brand=${props.brandParam}` : ''}`}
              viewAllLabel="View all"
            />
          )}
        </main>

        {/* ── Mobile TOC pill (floating pill + bottom-sheet) ── */}
        {tocItems.length >= 2 && <BlogTOC items={tocItems} mobileSheet />}

        <BlogBackToTop />

        {isFlow ? <FlowFooter /> : isComet ? <CometFooter /> : isGalaxy ? <GalaxyFooter /> : isGravity ? <GravityFooter /> : isStoryhealth ? <StoryhealthFooter /> : <Footer theme="light" />}
      </div>
    </ErrorBoundary>
  );
}
